import React, { FC } from "react"

import { ActionIcon, Group, MultiSelect, Text, Tooltip } from "@mantine/core"
import {
    IconBoxModel,
    IconBoxMultiple, IconBoxMultiple9, IconBoxOff,
    IconCards,
    IconGraph,
    IconNetwork,
    IconTrash,
    IconUserDown,
    IconUsersGroup,
    IconUserUp
} from "@tabler/icons-react"

import { useAuth, User } from "../../../contexts/authentication-context"
import { useGroups } from "../../../contexts/devices-context"
import { useLanguage } from "../../../contexts/language-context"
import useUsersSettings from "./useUsersSettings"
import RequiresRole from "../../auth/components/require-auth/RequiresRole"

type UsersSettingsItemProps = { user: User }

const UsersSettingsItem: FC<UsersSettingsItemProps> = ({ user }) => {
    const { groupClaims, setGroupClaims, roleClaims, setRoleClaims, onDelete, processingDelete, processingUpdate } =
        useUsersSettings(user)
    const { data: groups } = useGroups()
    const { user: currentUser, hasRole } = useAuth()
    const { L } = useLanguage()

    return (
        <tr>
            <td>
                <Text sx={{ whiteSpace: "nowrap" }} weight={user.uid === currentUser?.uid ? "bold" : "inherit"}>
                    {user.displayName ?? "—"}
                </Text>
            </td>
            <td>
                <Text sx={{ whiteSpace: "nowrap" }} weight={user.uid === currentUser?.uid ? "bold" : "inherit"}>
                    {user.email ?? "—"}
                </Text>
            </td>
            <td>
                {user.roles === true ? (
                    <Text sx={{ whiteSpace: "nowrap" }}>{L("Full access")}</Text>
                ) : (
                    <MultiSelect
                        data={[
                            {value: "edit-devices", label: L("Edit devices")},
                        ]}
                        value={roleClaims === true ? [] : roleClaims}
                        placeholder={"View only"}
                        onChange={setRoleClaims}
                        disabled={processingUpdate}
                        readOnly={!hasRole("admin") || currentUser?.uid == user.uid}
                        withinPortal
                    />
                )}
            </td>
            <td>
                {user.groups ? (
                    user.groups === true ? (
                        <Text sx={{ whiteSpace: "nowrap" }}>{L("Full access")}</Text>
                    ) : (
                        <MultiSelect
                            data={groups.map(({ id, name }) => ({ value: id, label: name }))}
                            value={groupClaims === true ? [] : groupClaims}
                            placeholder={L("No groups")}
                            onChange={setGroupClaims}
                            disabled={processingUpdate}
                            readOnly={!hasRole("admin") || currentUser?.uid == user.uid}
                            withinPortal
                        />
                    )
                ) : (
                    "—"
                )}
            </td>
            <td>
                {hasRole("admin") && (
                    <Group spacing="xs" noWrap>
                        <Tooltip
                            label={user.roles === true ? L("Revoke full access") : L("Grant full access")}
                            withinPortal
                        >
                            <ActionIcon
                                onClick={() => setRoleClaims(user.roles === true ? [] : true)}
                                disabled={currentUser?.uid === user.uid}
                                loading={processingUpdate}
                                variant="outline"
                            >
                                {user.roles === true ? <IconUserDown size={18} /> : <IconUserUp size={18} />}
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip
                            label={user.groups === true ? L("Revoke all groups") : L("Grant all groups")}
                            withinPortal
                        >
                            <ActionIcon
                                onClick={() => setGroupClaims(user.groups === true ? [] : true)}
                                disabled={currentUser?.uid === user.uid}
                                loading={processingUpdate}
                                variant="outline"
                            >
                                {user.groups === true ? <IconBoxOff size={18} /> : <IconBoxMultiple size={18} />}
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label={L("Delete user")} withinPortal>
                            <ActionIcon
                                color="red"
                                variant="filled"
                                onClick={onDelete}
                                loading={processingDelete}
                                disabled={currentUser?.uid === user.uid}
                            >
                                <IconTrash size={18} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                )}
            </td>
        </tr>
    )
}

export default UsersSettingsItem
